import { type FC, useEffect, useState, useCallback } from 'react'
import type WaveSurfer from 'wavesurfer.js'
import classNames from 'classnames'
import { observer } from 'mobx-react-lite'
import { secondsToTime } from 'shared/lib/secondsToTime'
import { Typography, SpeedMediaBtn, type IIconButtonSize } from 'shared/ui'
import { uiStore } from 'shared/store/uiStore'
import { getAriaLabel } from 'shared/lib'
import { AudioPlayerActions } from 'shared/ui/AudioPlayer/ui/AudioPlayerActions'
import { AudioWave } from 'shared/ui/AudioPlayer/ui/AudioWave/AudioWave'
import {
  type IPlayerColorType,
  type IPlayerSizeType,
  type IResponseRecordTranscriptStatus,
} from 'shared/ui/AudioPlayer/ui/types'
import { SectionBottom } from 'shared/ui/AudioPlayer/ui/SectionBottom/SectionBottom'
import styles from './styles.module.scss'
import { PlayAttachmentButton, PlayDefaultButton } from './AudioPlayButton'

export type IAudioPlayerTranscriptItem = {
  id: number
  speaker_label: string
  speaker_number: number
  text: string
}

export type IAudioPlayerSummaryItem = {
  id: number
  title: string
  items: {
    id: number
    text: string
  }[]
}

type IAudioPlayerRecord = {
  id: number
  record_id: string
  sid: string
}

export type IHandleTrackOrganizationActivity = {
  event: string
  data?: {
    [key: string]: string | number | number[]
  }
}

export type AudioPlayerProps = {
  url?: string
  fileName?: string
  summary?: IAudioPlayerSummaryItem[]
  transcriptions?: IAudioPlayerTranscriptItem[]
  transcriptStatus?: IResponseRecordTranscriptStatus
  is_auto_transcribe?: boolean
  id?: string
  fromAttachMedia?: boolean
  widthWave?: number
  heightWave?: number
  isClosed?: boolean
  handlePlaying?: (value: boolean) => void
  colorType?: IPlayerColorType
  size?: IPlayerSizeType
  fullWidth?: boolean
  noRightActions?: boolean
  autoplay?: boolean
  togglePlaying?: (value: boolean) => void
  setScrollBottomTrigger?: () => void
  retryTranscript?: () => void
  className?: string
  canTranscript?: boolean
  disableTranscript?: boolean
  hideTranscriptionButton?: boolean
  fromCallsHistory?: boolean
  isCallAudio?: boolean
  record?: IAudioPlayerRecord
  contactId?: number
  handleTrackOrganizationActivity?: (data: IHandleTrackOrganizationActivity) => void
  isHipaaCompliant?: boolean
  hidePlayerActions?: boolean
  source?: string
}

export const AudioPlayer: FC<AudioPlayerProps> = observer((props) => {
  const {
    url,
    id,
    summary = [],
    transcriptions = [],
    transcriptStatus = null,
    fromAttachMedia,
    widthWave,
    heightWave,
    isClosed = false,
    handlePlaying,
    colorType = 'white',
    size = 'middle',
    fullWidth,
    noRightActions,
    autoplay,
    togglePlaying,
    className,
    hideTranscriptionButton = false,
    fromCallsHistory,
    isCallAudio = false,
    record,
    contactId,
    handleTrackOrganizationActivity,
    isHipaaCompliant,
    hidePlayerActions = false,
  } = props
  const [showTranscription, setShowTranscription] = useState(false)
  const [waveform, setWaveform] = useState<WaveSurfer | null>(null)
  const [speed, setSpeed] = useState(1)
  const [duration, setDuration] = useState<number | null>(null)
  const [playing, setPlaying] = useState(!!autoplay)
  const showShortView = isClosed && !playing

  const onPlay = useCallback(() => {
    if (waveform) {
      uiStore.setPlayAudioId(id || url || '')
      if (playing) {
        waveform.pause()
      } else {
        waveform.play()
      }
      setPlaying(!playing)
      handlePlaying?.(!playing)
    }
  }, [waveform, playing])

  const onSpeed = (speed: number) => {
    setSpeed(speed)
    waveform?.setPlaybackRate(speed)
  }

  const PlayButton = fromAttachMedia ? PlayAttachmentButton : PlayDefaultButton

  const iconButtonSize = (): IIconButtonSize => {
    if (size === 'small') return 'small'

    return 'medium'
  }

  useEffect(() => {
    togglePlaying?.(playing)
    if (isCallAudio && isHipaaCompliant && playing && record?.record_id) {
      handleTrackOrganizationActivity?.({
        event: 'phi.call.recording.accessed',
        data: {
          id: record.id,
          recording_id: record.record_id,
        },
      })
    }
  }, [playing, isCallAudio])

  useEffect(() => {
    if (isCallAudio && isHipaaCompliant && showTranscription && record?.sid && contactId) {
      handleTrackOrganizationActivity?.({
        event: 'phi.call.transcript.viewed',
        data: {
          call_id: record?.sid,
          contact_id: contactId,
        },
      })
    }
  }, [showTranscription, isCallAudio])

  useEffect(() => {
    setDuration(null)
  }, [url, id])

  const audioWave = url && (
    <AudioWave
      url={url}
      waveform={waveform}
      setWaveform={setWaveform}
      id={id || url}
      playing={playing}
      setPlaying={setPlaying}
      setDuration={setDuration}
      width={widthWave}
      height={heightWave}
      hide={showShortView}
      autoplay={autoplay}
    />
  )

  return (
    <>
      <div
        className={classNames(
          styles.wrapAudio,
          styles[`size_${size}`],
          styles[`color_${colorType}`],
          {
            [styles.fromAttachMedia]: fromAttachMedia,
            [styles.fullWidth]: fullWidth,
            [styles.noRightActions]: noRightActions,
            [styles.fromCallsHistory]: fromCallsHistory,
          },
          className
        )}
        aria-label={getAriaLabel('AudioPlayer')}
      >
        <PlayButton isPlaying={playing} onClick={onPlay} size={iconButtonSize()} />

        <Typography
          variant={'body-md-regular'}
          width={duration !== null ? 46 : 84}
          className={styles.duration}
          tag={'div'}
          ariaLabel={'time'}
        >
          {duration !== null ? secondsToTime(duration) : 'Processing...'}
        </Typography>
        {url && fullWidth ? <div className={styles.wave}>{audioWave}</div> : audioWave}

        <div className={styles.actions}>
          {!fromAttachMedia && !showShortView && !noRightActions && (
            <>
              <div className={styles.wrapSpeed}>
                <SpeedMediaBtn fromAudioPlayer speed={speed} setSpeed={onSpeed} />
              </div>
              {!hidePlayerActions && (
                <AudioPlayerActions
                  {...props}
                  showTranscription={showTranscription}
                  setShowTranscription={setShowTranscription}
                  hideTranscriptionButton={hideTranscriptionButton}
                />
              )}
            </>
          )}
        </div>
      </div>
      {showTranscription && (
        <SectionBottom
          transcriptStatus={transcriptStatus}
          transcriptions={transcriptions}
          summary={summary}
        />
      )}
    </>
  )
})
